import { Grid, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { motion, AnimatePresence } from 'framer-motion';
import { useEffect, useState } from 'react';
import { Album } from '../database/music';
import { Playlist } from '../database/videos';

export interface MediaItemProps {
    album?: Album;
    playlist?: Playlist;
}

export interface Media {
    external_url: string;
    image_url: string;
    name: string;
    release_date: string;
}

const MediaItem: React.FC<MediaItemProps> = ({ album, playlist }) => {
    const theme = useTheme();
    const [mediaObj, setMediaObj] = useState<Media>();
    const [isHovered, setIsHovered] = useState(false);

    useEffect(() => {
        if (album) {
            setMediaObj({
                external_url: album.external_urls.spotify,
                image_url: album.images[1].url,
                name: album.name,
                release_date: album.release_date,
            });
        } else if (playlist) {
            setMediaObj({
                external_url: `https://www.youtube.com/watch?v=${playlist.contentDetails.videoId}`,
                image_url: playlist.snippet.thumbnails.medium.url,
                name: playlist.snippet.title,
                release_date: playlist.snippet.publishedAt.toString(),
            });
        }
    }, [album, playlist]);

    const handleClick = (url: string) => {
        window.open(url, '_blank');
    };

    if (!mediaObj) {
        return null;
    }

    return (
        <Grid
            item
            xs={12}
            sm={6}
            md={6}
            lg={4}
            sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                marginBottom: '50px',
            }}
        >
            <motion.div
                initial={{ opacity: 0, scale: 0.92 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                whileHover={{ scale: 1.04, y: -4 }}
                whileTap={{ scale: 0.97 }}
                transition={{ type: 'spring', stiffness: 280, damping: 20 }}
                onHoverStart={() => setIsHovered(true)}
                onHoverEnd={() => setIsHovered(false)}
                onClick={() => handleClick(mediaObj.external_url)}
                style={{
                    cursor: 'pointer',
                    position: 'relative',
                    borderRadius: '10px',
                    overflow: 'hidden',
                    border: `1px solid ${theme.palette.primary.main}`,
                    boxShadow: isHovered
                        ? `0 0 18px 4px ${theme.palette.primary.main}60`
                        : '0 2px 8px rgba(0,0,0,0.4)',
                    transition: 'box-shadow 0.3s ease',
                    display: 'inline-block',
                }}
            >
                <motion.img
                    src={mediaObj.image_url}
                    alt={mediaObj.name}
                    animate={{ opacity: isHovered ? 0.12 : 1 }}
                    transition={{ duration: 0.25 }}
                    style={{
                        borderRadius: '10px',
                        display: 'block',
                        width: '100%',
                        height: 'auto',
                    }}
                />
                <AnimatePresence>
                    {(isHovered || playlist) && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.2 }}
                            style={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                width: '100%',
                                textAlign: 'center',
                                padding: '8px',
                            }}
                        >
                            <Stack
                                alignItems='center'
                                sx={{
                                    backgroundColor: playlist && !isHovered
                                        ? `${theme.palette.background.default}CC`
                                        : '',
                                    borderRadius: playlist ? '20px' : '',
                                    padding: playlist ? '6px 10px' : '',
                                }}
                            >
                                <Typography
                                    variant='body1'
                                    sx={{ color: theme.palette.secondary.main, fontWeight: 'bold' }}
                                >
                                    {mediaObj.name}
                                </Typography>
                                <Typography
                                    variant='body2'
                                    sx={{ color: theme.palette.secondary.main, opacity: 0.8 }}
                                >
                                    {new Date(mediaObj.release_date).toLocaleDateString()}
                                </Typography>
                            </Stack>
                        </motion.div>
                    )}
                </AnimatePresence>
            </motion.div>
        </Grid>
    );
};

export default MediaItem;
